"use client";

import moment from 'moment';
import * as duckdb from '@duckdb/duckdb-wasm';
import MainController from "./MainController";
import { runPipeline } from './Pipeline';
import embed from "vega-embed";
import { io, Socket } from "socket.io-client";
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { updateDoc, getFirestore, collection, doc, getDoc, query, where, getDocs, Firestore, addDoc, documentId } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import XLSX from 'xlsx'


import { ISCHART_STUDIO_GS, ISCHART_INFORMATICS_GS } from "@/informatics/config";



export type State = {
  width?: number,
  height?: number,
  loggedin?: boolean,
  duckLoaded?: boolean,
  datasetList?: any[],
  templateList?: any[],
  chartInfo?: any,
  datasetId?: string | null,
  dimList?: string[],
  filename?: string | null,
  agg?: string | null,
  selectedTab?: string | null,
  showChart?: boolean,
  filter?: string | null,
  colorValues?: string[];
  socketMessage?: any,
  allDivisionRead?: boolean,
  selectedDivision?: any,
}

export const StateInitial: State = {
  width: window.innerWidth,
  height: window.innerHeight,
  loggedin: false,
  duckLoaded: false,
  datasetList: [],
  templateList: [],
  chartInfo: {},
  datasetId: null,
  dimList: [],
  filename: null,
  agg: null,
  selectedTab: null,
  showChart: false,
  filter: null,
  colorValues: [],
  socketMessage: null,
  allDivisionRead: false,
  selectedDivision: null,
}

export type Event = 
  { message: "DidMount", params: any }
  | { message: "DidUpdate", params: any }
  | { message: "Login", params: any }
  | { message: "Logout", params: any }
  | { message: "GetDatasetList", params: {} }
  | { message: "GetChartData", 
      params: {datasetId: string, dimList: string[], setData: any} }
  | { message: "GetChartInfo", params: {datasetList: string[]} }
  | { message: "GetTemplateList", params: {} }
  | { message: "AddTemplate", params: {template: any} }
  | { message: "GenInformatics", 
      params: { 
        arrayBuffer: ArrayBuffer, setProgress: any, setImageUrl: any, template: any
      }
    }
  | { message: "GetFields", params: { templateId: string, setFields: any } }
  | { message: "GetRecipe", params: { recipeId: string, setRecipe: any } }
  | { message: "AddRecipeItem", params: { item: any, setRecipe: any } }
  
export type Data = {
  duckInstance?: duckdb.AsyncDuckDB,
  duckCon?: duckdb.AsyncDuckDBConnection,
  socket: Socket | null,
}

export const DataInitial = {
  socket: null,
}


type Handler = (controller: MainController, params?: any) => any




export const DidMount: Handler = async (controller, params) => { 
  SetupFirebaseAuth(controller, params);
  SetupDuckDb(controller, params);
  SetupSocket(controller, params);
}

export const Login: Handler = async (controller, params) => {
  const auth = getAuth(controller.app);
  const provider = new GoogleAuthProvider();
  await signInWithPopup(auth, provider);
}

export const Logout: Handler = async (controller, params) => {
  const auth = getAuth(controller.app);
  await signOut(auth);
  controller.user = null;
  controller.setState({loggedin: false});
}

const SetupFirebaseAuth: Handler = async (controller, params) => {
  let auth = getAuth(controller.app);
  
  const onFirebaseAuthStateChanged = async (user: any) => {
    if (user) {
      // console.log(user.email)
      auth = getAuth(controller.app);
      controller.user = auth.currentUser;
      controller.setState({loggedin: true});      
      GetDatasetList(controller, {});
    } else {
      console.log("no user");
    }
  }
  
  onAuthStateChanged(auth, onFirebaseAuthStateChanged);
}

const SetupDuckDb: Handler = async (controller, params) => {
  // Set up duckdb -----------------------------------------------------
  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
  const worker_url = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker!}");`], {type: 'text/javascript'})
  );
  const worker = new Worker(worker_url);
  const logger = new duckdb.ConsoleLogger();
  let duckInstance = new duckdb.AsyncDuckDB(logger, worker);
  await duckInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);
  URL.revokeObjectURL(worker_url);

  // const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
  //   mvp: {
  //       mainModule: `/duckdb/duckdb-mvp.wasm`,
  //       mainWorker: `/duckdb/duckdb-browser-mvp.worker.js`,
  //   },
  //   eh: {
  //       mainModule: `/duckdb/duckdb-eh.wasm`,
  //       mainWorker: `/duckdb/duckdb-browser-eh.worker.js`,
  //   },
  // };
  // const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
  // // Instantiate the asynchronous version of DuckDB-Wasm
  // const worker = new Worker(bundle.mainWorker!);
  // const logger = new duckdb.ConsoleLogger();
  // const duckInstance = new duckdb.AsyncDuckDB(logger, worker);
  // await duckInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);
 
  let duckCon = await duckInstance.connect();
  controller.data.duckInstance = duckInstance;
  controller.data.duckCon = duckCon;
  controller.setState({duckLoaded: true});
}

const SetupSocket: Handler = async (controller, params) => {
  if (!controller.data.socket) {
    controller.data.socket = io('http://localhost:30000/', {
      transports: ['websocket'], // Force WebSocket transport
      reconnectionAttempts: 5, // Number of reconnection attempts
      reconnectionDelay: 1000, // Delay between reconnections
    });
    controller.data.socket.on('connect', () => {
      console.log("connected");
    });
    controller.data.socket.on('message', (message: any) => {
      controller.setState({
        socketMessage: {
          ...message,
          message: (controller.getState().socketMessage?.message || "") + (message?.message || "")
        }
      });
    });
  }
}

export const DidUpdate: Handler = async (controller, params) => {
}

export const GetDatasetList: Handler = async (controller, params) => {
  let datasetList: any[] = [];
  let allDivisionRead = false

  try {
    if (controller.useCloud) {

      const db: Firestore = controller.db;

      let auth = getAuth()
      let uid = auth?.currentUser?.uid
      console.log('uid: ', uid);
      let users = [Number(uid), uid]
      let email = controller.user.email 
      if (email) {
        users.push(email)
      }

      const dsQuery = query(
        collection(db, "IFT_dataset"),
        where("users", "array-contains-any", users)
      );

      const res = await getDocs(dsQuery);
      console.log('res: ', res);

      datasetList = res.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((d: any) => !d.inactive);

      console.log('datasetList: ', datasetList);

      // อ่าน IFT_dataset แล้วดูตัวเองใน users เพื่อเข้าใช้งาน ทุกคนต้องเข้าไปดูได้ 
      // อ่าน IFT_permission เพื่อดูว่าสามารถ เข้าถึงทุกๆ divisions หรือไม่ โดยหาจาก uid และ email (backward)

      let docIds = [uid]
      if (email) {
        docIds.push(email)
      }
      console.log('docIds: ', docIds);

      const IFT_permissionQuery = query(
        collection(db, "IFT_permission"), where(documentId(), "in", docIds)
      );

      const IFT_permissionQuerySnapshot = await getDocs(IFT_permissionQuery);
      IFT_permissionQuerySnapshot.forEach((doc) => {
        allDivisionRead = allDivisionRead || doc.data()?.allDivisionRead
        console.log("IFT_permissionQuerySnapshot", doc.id, " => ", doc.data()?.allDivisionRead);
        // console.log('IFT_permissionQuerySnapshot allDivisionRead: ', allDivisionRead);
      });


    } else {
      // Local JSON fallback
      console.log('Local JSON fallback: ');
      allDivisionRead = true
      const datasetListObject = await (await fetch(`/data/dataset.json`)).json();
      datasetList = datasetListObject.items ?? [];
    }
    // console.log('GetDatasetList datasetList: ', datasetList);

    // Stable, locale-safe sort by id
    datasetList.sort((a: any, b: any) =>
      String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
    );

    controller.setState({ datasetList, allDivisionRead });

    // keep your downstream call
    await GetChartInfo(controller, { datasetList, ...params });
  } catch (err: any) {
    console.error("GetDatasetList failed:", err);
    // Optional: surface a minimal error state for UI
    controller.setState({ datasetList: [] });
  }
};

export const queryDuck = async (sql: string, con: duckdb.AsyncDuckDBConnection | null) => {
  if (!con) return [];
  let res = await con.query(sql);
  let output = (res?.toArray() || []).map((item: any) => item.toJSON());
  return output
}

const isRecent = (cur: any) => {
  return (
    cur?.filter?.y === moment().format('YYYY') 
    || cur?.filter?.ym === moment().format('YYYY-MM')
  );
}

type AggInfo = {
  [name: string] : {
    one: any[],
    onefilter: Map<string, any>
    two: any[],
    twofilter: Map<string, any>
  }
}

export const GetChartInfo: Handler = async (controller, params) => {
  let {datasetList} = params;
  // console.log('GetChartInfo params: ', params);
  // console.log('GetChartInfo datasetList: ', datasetList);
  if (!datasetList) return console.log("No datasetList supplied");
  
  let chartInfo: any = {};
  datasetList.map(async (dataset: any) => {
    const datasetId = dataset.id;
    
    let infoUrl: string;
    if (controller.useCloud) {
      const storageRef = ref(controller.storage, `${ISCHART_INFORMATICS_GS}/${datasetId}/chartinfo.json`);
      infoUrl = await getDownloadURL(storageRef);
    } else {
      infoUrl = `/data/${datasetId}/chartinfo.json`;
    }

    const chartInfoItem = await (await fetch(infoUrl)).json();
    chartInfo[datasetId] = chartInfoItem;
    console.log('GetChartInfo chartInfo: ', chartInfo);
    chartInfo = Object.fromEntries(
      Object.entries(chartInfo).sort((a: any, b: any) => a?.[0] < b?.[0] ? -1 : 1));
    controller.setState({chartInfo});
  });
};

export const GetChartData: Handler = async (controller, params) => {
  console.log('GetChartData params: ', params);
  // Get parquet file ----------------------------------------------------
  let {duckInstance, duckCon} = controller.data;
  let {filename, datasetId, dimList, setData, setForExcel, setSpec, agg, prefix} = params;
  if (!duckInstance || !duckCon || !datasetId || !dimList || !setData || !setSpec || !agg) 
    return console.log("Insufficient params.", duckInstance, duckCon, dimList, setData);
  await duckInstance.dropFiles();
  
  let spec: any;
  let info: ArrayBuffer;
  if (controller.useCloud) {
    // const storage = getStorage(controller.app);
    
    // Get chart data
    const chartDataRef = ref(
      controller.storage, 
      `${ISCHART_INFORMATICS_GS}/${datasetId}${prefix}/chartdata/${filename}.parquet`
    );
    const infoUrl = await getDownloadURL(chartDataRef);
    info = await (await fetch(infoUrl)).arrayBuffer();
    
    // Get spec
    const specRef = ref(controller.storage, `${ISCHART_INFORMATICS_GS}/${datasetId}/spec.json`);
    const specUrl = await getDownloadURL(specRef);
    spec = await (await fetch(specUrl)).json();
  } else {
    info = await (await fetch(
      `/data/${datasetId}${prefix}/chartdata/${filename}.parquet`))
      .arrayBuffer();
    spec = await (await (fetch(`/data/${datasetId}/spec.json`))).json();    
  }
  setSpec(spec);
  
  await duckInstance.dropFiles();
  await duckInstance.registerFileBuffer(`${filename}.parquet`, new Uint8Array(info));

  // let test = await (await fetch("/api/getencounter/")).arrayBuffer();
  // await duck.registerFileBuffer('original.parquet', new Uint8Array(test));

  // Query data ----------------------------------------------------------
  await duckCon.query(`
    create or replace table chartdata as 
    select ${dimList.join(",")}, ${agg}
    from read_parquet('${filename}.parquet');`);

  console.log("filename", filename);
  let output = await queryDuck(`from chartdata`, duckCon);
  console.log("output", output);
  let forExcel: any[] = [];
  if (dimList.length === 1) {
    let pivotSql = `pivot chartdata on ${dimList[0]} using last(${agg})`
    forExcel = await queryDuck(pivotSql, duckCon);
  } else if (dimList.length === 2) {
    let pivotSql = `
    pivot chartdata 
      on ${dimList[0]} 
      using last(${agg}) 
      group by ${dimList[1]}
      order by ${dimList[1]}`
    forExcel = await queryDuck(pivotSql, duckCon);
  }
  forExcel = forExcel.map((row: any) => {
    return Object.fromEntries(
      Object.entries(row)
      .map((entry: any) => (
        [entry[0], entry[1] === null ? 0 : entry[1]]
      ))
    )
  });
  if (forExcel.length > 0) {
    forExcel = [Object.keys(forExcel[0])].concat(
      forExcel.map((row: any) => Object.values(row))
    )
  }
  
  // Set state -----------------------------------------------------------
  setData(output.map((item: any) => ({
    ...item, 
    [agg]: Number(item[agg]),
  })));
  setForExcel(forExcel);
}

export const GetTemplateList: Handler = async (controller, params) => {
  const templateRef = collection(controller.db, "IFT_template");
  const result = await getDocs(templateRef);
  let templateList = result.docs.map((doc: any) => ({id: doc.id, ...doc.data()}));
  controller.setState({templateList});
}

export const AddTemplate: Handler = async (controller, params) => {
  let { template } = params;
  if (!template) return console.log("Insufficient params");
  const templateRef = collection(controller.db, "IFT_template");
  await addDoc(templateRef, template);
}

export const GenInformatics: Handler = async (controller, params) => {
  let {duckInstance, duckCon} = controller.data;
  let { arrayBuffer, setProgress, setImageUrl, template } = params;
  if (!duckInstance || !duckCon || !arrayBuffer || !setProgress) 
    return console.log("No duck or insufficient params");
  if (!template) {
    const templateDocRef = doc(controller.db, "IFT_template", "cnmi");
    const templateDoc = await getDoc(templateDocRef);
    template = templateDoc.data();
  }
  await duckInstance.registerFileBuffer('base.parquet', new Uint8Array(arrayBuffer));
  await duckCon.query(`
    create or replace table base as 
    from read_parquet('base.parquet');`);
  // let result = await query(`from base select count(*)`, duckCon);
  let sequence = await runPipeline(template, duckCon, setProgress);
  let data = await queryDuck(`from ${sequence[0]}`, duckCon);
  GenerateThumbnail(data, sequence[0], setImageUrl);
}

const GenerateThumbnail = async (data: any, dim: string, setImageUrl: any) => {
  data = data.map((item: any) => ({...item, id__count: Number(item.id__count)}));
  let temp = document.createElement("div")
  let res = await embed(temp, {
    data: {values: data},
    title: "Encounter Count",
    width: 900,
    height: 500,
    mark: "bar",
    encoding: {
      x: { 
        field: dim, 
        type: "ordinal", 
        sort: {field: dim, order: "ascending"} 
      },
      y: { 
        field: "id__count", 
        type: "quantitative"
      },
    }
  });
  let url = await res.view.toImageURL("png");
  setImageUrl(url);
  // DownloadImage(url, temp);
}

const DownloadImage = (url: string, temp: any) => {
  let link = document.createElement("a");
  link.href = url;
  link.download = "image_download.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  temp.remove();
}

export const GetFields: Handler = async (controller, params) => {
  let { duckInstance, duckCon } = controller.data;
  let { templateId, setFields } = params;
  if (!duckInstance || !duckCon || !templateId || !setFields) 
    return console.log("No duck or insufficient params");
  
  const storageRef = ref(controller.storage, `${ISCHART_STUDIO_GS}/template/${templateId}/fields.parquet`);
  const downloadUrl = await getDownloadURL(storageRef);
  
  let fields = await (await fetch(downloadUrl)).arrayBuffer();
  await duckInstance.registerFileBuffer('fields.parquet', new Uint8Array(fields));
  await duckCon.query(`
    create or replace table fields as 
    from read_parquet('fields.parquet');`);
  let res = await queryDuck("from fields", duckCon);
  setFields(res);
}

export const GetRecipe: Handler = async (controller, params) => {
  let { recipeId, setRecipe } = params;
  if (!recipeId || !setRecipe) return console.log("Insufficient params");
  
  const recipeDocRef = doc(controller.db, "IFT_recipe", recipeId);
  const recipeDoc = await getDoc(recipeDocRef);
  const data = recipeDoc.data();
  setRecipe(data);
}

export const AddRecipeItem: Handler = async (controller, params) => {
  let { recipeId, item, setRecipe } = params;
  if (!recipeId || !item || !setRecipe) return console.log("Insufficient params");
  

  const recipeDocRef = doc(controller.db, "IFT_recipe", recipeId);
  const recipeDoc = await getDoc(recipeDocRef);
  let data = recipeDoc.data() || {items: []};
  data.items.push(item);
  
  await updateDoc(recipeDocRef, {items: data.items || []});
  setRecipe(data);
}
